import React, { useEffect, useRef, useState } from "react";
import { Plus, Search, Loader2, PackagePlus, X } from "lucide-react";
import api, { formatMoney2, formatNumber } from "../../lib/api";

const ESTADOS = {
  vendido: ["Se ha vendido", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  sin_ventas: ["Sin ventas en el periodo", "bg-amber-50 text-amber-700 border-amber-200"],
  nunca: ["Nunca se ha vendido", "bg-slate-100 text-slate-600 border-slate-200"],
  nuevo: ["Producto nuevo", "bg-sky-50 text-sky-700 border-sky-200"],
};

export function EstadoBadge({ r }) {
  const [txt, cls] = ESTADOS[r.estado] || ESTADOS.nunca;
  const extra = r.estado === "vendido" ? ` · ${formatNumber(r.uds_vendidas)} uds` : r.estado === "sin_ventas" && r.u_salida ? ` · última ${new Date(r.u_salida).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" })}` : "";
  return <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full border ${cls}`} data-testid={`estado-${r.cn}`}>{txt}{extra}</span>;
}

function NuevoForm({ onAdd, onCancel, cnInicial, existentes }) {
  const [f, setF] = useState({ cn: /^\d+$/.test(cnInicial) ? cnInicial : "", descripcion: /^\d+$/.test(cnInicial) ? "" : cnInicial, unidades: 1, coste: "" });
  const repetido = existentes.has(f.cn.trim());
  const ok = f.cn.trim() && f.descripcion.trim() && f.unidades > 0 && !repetido;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="mt-3 rounded-lg border border-sky-200 bg-sky-50/50 p-3 flex flex-wrap items-end gap-2" data-testid="la-nuevo-form">
      <label className="text-xs font-semibold text-slate-600 flex flex-col">CN<input className="input mt-1" style={{ width: 110 }} value={f.cn} onChange={set("cn")} data-testid="la-nuevo-cn" /></label>
      <label className="text-xs font-semibold text-slate-600 flex flex-col flex-1 min-w-[200px]">Nombre<input className="input mt-1 w-full" value={f.descripcion} onChange={set("descripcion")} data-testid="la-nuevo-nombre" /></label>
      <label className="text-xs font-semibold text-slate-600 flex flex-col">Unidades<input type="number" min={1} className="input mt-1" style={{ width: 90 }} value={f.unidades} onChange={(e) => setF({ ...f, unidades: Math.max(0, parseInt(e.target.value, 10) || 0) })} data-testid="la-nuevo-uds" /></label>
      <label className="text-xs font-semibold text-slate-600 flex flex-col">Coste ud. (€)<input type="text" inputMode="decimal" placeholder="0,00" className="input mt-1" style={{ width: 110 }} value={f.coste} onChange={set("coste")} data-testid="la-nuevo-coste" /></label>
      <button disabled={!ok} className="btn btn-primary btn-sm" data-testid="la-nuevo-anadir"
              onClick={() => onAdd({ cn: f.cn.trim(), descripcion: f.descripcion.trim(), familia: null, abcd: null, stock: 0, venta_diaria: 0,
                                    unidades: f.unidades, coste: Math.round((parseFloat(String(f.coste).replace(",", ".")) || 0) * 100) / 100, manual: true, estado: "nuevo" })}>
        <Plus size={14} /> Añadir</button>
      <button onClick={onCancel} className="btn btn-outline btn-sm" data-testid="la-nuevo-cancelar">Cancelar</button>
      {repetido && <p className="w-full text-xs font-semibold text-amber-700" data-testid="la-nuevo-repetido">Ese CN ya está en el pedido: cambia sus unidades en la tabla.</p>}
    </div>
  );
}

export function AnadirProducto({ lab, dias, existentes, onAdd }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [nuevo, setNuevo] = useState(false);
  const t = useRef();

  useEffect(() => {
    clearTimeout(t.current);
    if (q.trim().length < 2) { setRes(null); return; }
    t.current = setTimeout(() => {
      setCargando(true);
      api.get("/lab-analisis/buscar", { params: { laboratorio: lab, q: q.trim(), dias } })
        .then((r) => setRes(r.data.productos)).catch(() => setRes([])).finally(() => setCargando(false));
    }, 300);
  }, [q, lab, dias]);

  const add = (p) => { onAdd(p); setQ(""); setRes(null); setNuevo(false); };
  return (
    <div className="mb-4 rounded-xl border border-dashed border-slate-300 p-4" data-testid="la-anadir">
      <div className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2"><PackagePlus size={16} className="text-sky-700" /> Añadir un producto al pedido</div>
      <div className="relative max-w-xl">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input className="input w-full" style={{ paddingLeft: 36 }} placeholder={`Busca por nombre o CN en ${lab}…`} value={q} onChange={(e) => setQ(e.target.value)} data-testid="la-anadir-buscar" />
        {cargando && <Loader2 size={14} className="animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />}
        {res && (
          <div className="absolute z-20 mt-1 w-full max-h-80 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-xl" data-testid="la-anadir-resultados">
            {res.map((p) => {
              const ya = existentes.has(p.cn);
              return (
                <button key={p.cn} disabled={ya} onClick={() => add(p)} data-testid={`la-anadir-opcion-${p.cn}`}
                        className="w-full text-left px-3 py-2 border-b border-slate-100 hover:bg-sky-50 disabled:opacity-50 disabled:hover:bg-white transition-colors">
                  <div className="text-sm font-semibold">{p.descripcion}</div>
                  <div className="text-[11px] text-slate-500 flex items-center gap-2 flex-wrap mt-0.5">
                    CN {p.cn} · stock {formatNumber(p.stock)} · {formatMoney2(p.coste)} <EstadoBadge r={p} />{ya && <b className="text-slate-700">Ya está en el pedido</b>}
                  </div>
                </button>
              );
            })}
            <button onClick={() => { setNuevo(true); setRes(null); }} className="w-full text-left px-3 py-2.5 text-sm font-semibold text-sky-700 hover:bg-sky-50" data-testid="la-anadir-nuevo">
              <Plus size={14} className="inline -mt-0.5" /> {res.length ? "¿No está? Añadir" : "No está en tus datos: añadir"} «{q}» como producto nuevo
            </button>
          </div>
        )}
      </div>
      {nuevo && <NuevoForm cnInicial={q.trim()} existentes={existentes} onAdd={add} onCancel={() => setNuevo(false)} />}
    </div>
  );
}

export function QuitarExtra({ onClick, cn }) {
  return <button onClick={onClick} aria-label="Quitar" className="ml-1 p-1 rounded text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid={`la-quitar-${cn}`}><X size={14} /></button>;
}
